/**
 * Redimensionado de imágenes en el navegador, antes de subirlas.
 *
 * POR QUÉ EXISTE
 * --------------
 * La tienda servía las fotos de productos por el endpoint de transformación de
 * Supabase (`/storage/v1/render/image/public/`). Ese endpoint se cobra por
 * imagen ORIGEN distinta transformada en el ciclo, no por request: el plan Pro
 * incluye 100 y llegamos a 680, lo que puso a la organización en
 * "exceeding usage limits" con riesgo de que los proyectos pasen a solo lectura.
 *
 * Achicando la foto en el momento de subirla, en el bucket queda directamente la
 * versión liviana y la tienda la sirve como objeto público común. El costo de
 * transformación desaparece y la optimización se mantiene.
 *
 * La escala del problema crece con el tamaño del catálogo, no con las visitas:
 * cada tienda nueva con dominio propio suma fotos. Por eso se resuelve de raíz y
 * no subiendo el límite.
 *
 * Test: tests/image-resize.test.mjs
 */

/**
 * Ancho máximo de una foto de producto.
 *
 * 900 y no 640: el consumidor más grande es ProductDetailModal, que pedía
 * `width: 720` a la transformación. Con un tope de 640 esa foto se vería
 * estirada. El resto pide menos —grilla 560, listado 360, carrito 128, POS y
 * listados del ERP unos 112— así que 900 los cubre a todos con margen.
 */
export const MAX_STORE_IMAGE_WIDTH = 900;

/** Misma calidad que usaba `quality=72` en el endpoint de render. */
export const STORE_IMAGE_QUALITY = 0.72;

const TIPOS_RESIZABLES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

/**
 * Dimensiones de destino manteniendo la proporción.
 *
 * @param {{ width?: number, height?: number, maxWidth?: number }} params
 * @returns {{ width: number, height: number }|null} null si no hay que tocarla.
 */
export const computeTargetSize = (params) => {
    // No se desestructura en la firma: el default `= {}` no aplica cuando el
    // llamador pasa `null` explícito, y ahí explotaría.
    if (!params || typeof params !== 'object') return null;

    const { width, height, maxWidth = MAX_STORE_IMAGE_WIDTH } = params;
    const w = Number(width);
    const h = Number(height);

    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;

    // Agrandar una foto chica no mejora nada y encima pesa más.
    if (w <= maxWidth) return null;

    const ratio = maxWidth / w;

    return {
        width: Math.round(maxWidth),
        // Piso de 1px: una panorámica extrema redondearía a 0 y canvas falla.
        height: Math.max(1, Math.round(h * ratio)),
    };
};

/**
 * ¿Vale la pena procesar este archivo?
 *
 * Los GIF quedan afuera a propósito: pasarlos por canvas les saca la animación.
 * Los SVG también, porque son vectores y no tienen tamaño que achicar.
 *
 * @param {{ type?: string, size?: number }|null} file
 * @returns {boolean}
 */
export const shouldResize = (file) => {
    if (!file || typeof file !== 'object') return false;
    return TIPOS_RESIZABLES.has(String(file.type || '').toLowerCase());
};

/**
 * Nombre del archivo resultante. Se guarda como webp, así que la extensión
 * tiene que acompañar: si no, el navegador recibe un content-type que no
 * coincide con lo que dice el nombre.
 *
 * @param {unknown} originalName
 * @returns {string}
 */
export const buildResizedFileName = (originalName) => {
    const nombre = String(originalName || '').trim();
    if (!nombre) return 'imagen.webp';

    const partes = nombre.split('.');
    if (partes.length === 1) return `${nombre}.webp`;

    partes.pop();
    return `${partes.join('.')}.webp`;
};

/**
 * Achica un archivo de imagen en el navegador y devuelve un File nuevo.
 *
 * Falla hacia el archivo original a propósito: si el navegador no puede
 * decodificar la imagen, es mejor subir la foto pesada que dejar al usuario sin
 * poder cargar su producto.
 *
 * @param {File} file
 * @param {{ maxWidth?: number, quality?: number }} [opciones]
 * @returns {Promise<File>}
 */
export const resizeImageFile = async (file, opciones = {}) => {
    const maxWidth = opciones.maxWidth || MAX_STORE_IMAGE_WIDTH;
    const quality = opciones.quality || STORE_IMAGE_QUALITY;

    if (!shouldResize(file)) return file;
    if (typeof document === 'undefined' || typeof createImageBitmap === 'undefined') return file;

    try {
        const bitmap = await createImageBitmap(file);
        const destino = computeTargetSize({ width: bitmap.width, height: bitmap.height, maxWidth });

        if (!destino) {
            bitmap.close?.();
            return file;
        }

        const canvas = document.createElement('canvas');
        canvas.width = destino.width;
        canvas.height = destino.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, destino.width, destino.height);
        bitmap.close?.();

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/webp', quality);
        });

        if (!blob) return file;
        // Si el "optimizado" pesa más que el original, no sirvió de nada.
        if (blob.size >= file.size) return file;

        return new File([blob], buildResizedFileName(file.name), {
            type: 'image/webp',
            lastModified: Date.now(),
        });
    } catch (error) {
        console.warn('[imageResize] no se pudo achicar, se sube el original', error);
        return file;
    }
};
